'use client'

/*
 * StroopTest
 *
 * Colour words appear in coloured ink. Name the ink, ignore the word.
 *
 * WHAT GETS SCORED. Not raw speed. The score is the interference cost: how much
 * slower you are when the word clashes with the ink (RED in blue) than when it
 * matches (RED in red). Subtracting one from the other is what makes this the
 * one tool on the site whose number survives a change of device. Screen
 * latency, input lag and a slow trackpad add the same delay to both conditions,
 * so they cancel in the difference. The reaction time test cannot do that.
 *
 * WHAT COUNTS AS A USABLE RESULT. Only correct answers are timed, and responses
 * under 200 ms (anticipations) or over 3000 ms (lapses) are dropped. Below 80%
 * accuracy the result is withheld rather than shown with a caveat, because
 * guessing fast is the easiest way to get a flattering interference score and
 * it would be measuring nothing.
 *
 * ABOUT THE COMPARISON. Same standard as the visual memory test. There is no
 * normative sample for a browser keypress Stroop, so the "less distracted than
 * about N%" figure is built from the typical shape of results on keypress
 * versions: a cost of roughly 50 to 150 ms for most adults. INTERFERENCE_MEAN
 * and INTERFERENCE_SD describe that shape. The UI calls it an estimate, never a
 * percentile.
 *
 * Carried over from the earlier tools: no tier renders grey, the result keeps
 * the game's colour, the CTA sits directly under the number, the score counts
 * up, and the prompt only mounts on the result.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import { Palette, RotateCcw, ArrowRight } from 'lucide-react'
import { normalCDF } from '@/lib/iqStats'
import TestPromptPopup from '@/components/TestPromptPopup'

// Inks are the 400 shades rather than the classic pure hues so each one stays
// readable on the dark stimulus card.
const COLOURS = [
  { id: 'red', word: 'RED', name: 'Red', ink: '#f87171', key: 'r' },
  { id: 'green', word: 'GREEN', name: 'Green', ink: '#4ade80', key: 'g' },
  { id: 'blue', word: 'BLUE', name: 'Blue', ink: '#60a5fa', key: 'b' },
  { id: 'yellow', word: 'YELLOW', name: 'Yellow', ink: '#facc15', key: 'y' },
]
const colourById = Object.fromEntries(COLOURS.map((c) => [c.id, c]))

const PRACTICE_PER_CONDITION = 2
const SCORED_PER_CONDITION = 20
const SCORED_TOTAL = SCORED_PER_CONDITION * 2
const FIXATION_MS = 400
const ERROR_FLASH_MS = 450
const MIN_RT = 200
const MAX_RT = 3000
const MIN_ACCURACY = 0.8

// The shape of typical results on keypress versions, not a normative sample.
// See the note at the top of the file.
const INTERFERENCE_MEAN = 90
const INTERFERENCE_SD = 50

// Lower interference is better, so tiers run from the smallest cost upwards.
const TIERS = [
  { max: 15, label: 'Unshakeable', sub: 'The words barely registered', ring: 'from-fuchsia-500 to-purple-600' },
  { max: 45, label: 'Laser focused', sub: 'Well beyond the usual', ring: 'from-cyan-500 to-blue-600' },
  { max: 75, label: 'Focused', sub: 'Better than typical', ring: 'from-emerald-500 to-teal-600' },
  { max: 130, label: 'Typical', sub: 'Around where most people land', ring: 'from-indigo-500 to-violet-600' },
  { max: 190, label: 'Word-pulled', sub: 'Reading kept getting in first', ring: 'from-amber-500 to-orange-600' },
  { max: Infinity, label: 'Word-bound', sub: 'The words won most rounds, try again', ring: 'from-rose-500 to-pink-600' },
]
const tierFor = (ms) => TIERS.find((t) => ms <= t.max)

const randomItem = (arr) => arr[Math.floor(Math.random() * arr.length)]

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function makeTrial(congruent, practice) {
  const ink = randomItem(COLOURS)
  const word = congruent ? ink : randomItem(COLOURS.filter((c) => c.id !== ink.id))
  return { congruent, practice, ink: ink.id, word: word.id }
}

// Equal numbers of matching and clashing trials, shuffled, so neither condition
// benefits from coming later when the player has warmed up.
function buildTrials() {
  const conditions = (n) => [...Array(n).fill(true), ...Array(n).fill(false)]
  return [
    ...shuffle(conditions(PRACTICE_PER_CONDITION)).map((c) => makeTrial(c, true)),
    ...shuffle(conditions(SCORED_PER_CONDITION)).map((c) => makeTrial(c, false)),
  ]
}

const mean = (xs) => xs.reduce((sum, x) => sum + x, 0) / xs.length

function scoreRun(results) {
  const scored = results.filter((r) => !r.practice)
  const correct = scored.filter((r) => r.correct)
  const timed = correct.filter((r) => r.rt >= MIN_RT && r.rt <= MAX_RT)
  const matching = timed.filter((r) => r.congruent).map((r) => r.rt)
  const clashing = timed.filter((r) => !r.congruent).map((r) => r.rt)
  const accuracy = correct.length / scored.length

  const base = { accuracy, correct: correct.length, total: scored.length }
  if (accuracy < MIN_ACCURACY || matching.length < 10 || clashing.length < 10) {
    return { ...base, usable: false }
  }

  const matchingMs = Math.round(mean(matching))
  const clashingMs = Math.round(mean(clashing))
  return { ...base, usable: true, matchingMs, clashingMs, interference: clashingMs - matchingMs }
}

export default function StroopTest() {
  const [phase, setPhase] = useState('idle') // idle | running | finished
  const [stage, setStage] = useState('fixation') // fixation | stimulus | error
  const [index, setIndex] = useState(0)
  const [result, setResult] = useState(null)
  const [best, setBest] = useState(null)
  const [isNewBest, setIsNewBest] = useState(false)
  const [countUp, setCountUp] = useState(0)

  // Trial state lives in refs as well, because responses arrive from a window
  // keydown listener and chained timeouts that would otherwise see stale values.
  const trials = useRef([])
  const results = useRef([])
  const indexRef = useRef(0)
  const stageRef = useRef('fixation')
  const shownAt = useRef(0)
  const bestRef = useRef(null)
  const rootRef = useRef(null)

  const timers = useRef([])
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  useEffect(() => clearTimers, [])

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('biqt_stroop_best')
      const parsed = stored === null ? null : parseInt(stored, 10)
      bestRef.current = Number.isFinite(parsed) ? parsed : null
      setBest(bestRef.current)
    } catch { setBest(null) }
  }, [])

  const setStageBoth = (s) => { stageRef.current = s; setStage(s) }

  const finish = useCallback(() => {
    clearTimers()
    const scored = scoreRun(results.current)
    setResult(scored)
    setPhase('finished')

    if (scored.usable && (bestRef.current === null || scored.interference < bestRef.current)) {
      setIsNewBest(bestRef.current !== null)
      bestRef.current = scored.interference
      setBest(scored.interference)
      try { window.localStorage.setItem('biqt_stroop_best', String(scored.interference)) } catch {}
    }
  }, [])

  const runTrial = useCallback((i) => {
    clearTimers()
    indexRef.current = i
    setIndex(i)
    setStageBoth('fixation')
    timers.current.push(setTimeout(() => {
      // Timestamped as the stimulus is committed rather than when it is
      // painted. The gap is the same for both conditions, so it cancels in the
      // interference score.
      shownAt.current = performance.now()
      setStageBoth('stimulus')
    }, FIXATION_MS))
  }, [])

  const respond = useCallback((colourId) => {
    if (stageRef.current !== 'stimulus') return
    const rt = performance.now() - shownAt.current
    const trial = trials.current[indexRef.current]
    const correct = colourId === trial.ink
    results.current.push({ ...trial, rt, correct })

    const next = indexRef.current + 1
    const advance = () => (next >= trials.current.length ? finish() : runTrial(next))

    if (correct) {
      advance()
    } else {
      setStageBoth('error')
      timers.current.push(setTimeout(advance, ERROR_FLASH_MS))
    }
  }, [finish, runTrial])

  useEffect(() => {
    if (phase !== 'running') return
    const onKey = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return
      const colour = COLOURS.find((c, i) => c.key === e.key.toLowerCase() || String(i + 1) === e.key)
      if (!colour) return
      e.preventDefault()
      respond(colour.id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [phase, respond])

  const start = () => {
    trials.current = buildTrials()
    results.current = []
    setResult(null)
    setIsNewBest(false)
    setPhase('running')
    runTrial(0)
    // On a phone the second row of colour buttons starts below the fold.
    // Scrolling mid-test would land in the timings, so bring the whole board
    // into view before the first word appears. Scrolled by hand rather than with
    // scrollIntoView, which ignored the scroll margin and parked the round
    // counter under the sticky nav. 80px clears the 64px nav with a gap.
    const top = rootRef.current?.getBoundingClientRect().top
    if (top !== undefined) window.scrollTo({ top: window.scrollY + top - 80, behavior: 'smooth' })
  }

  useEffect(() => {
    if (phase !== 'finished' || !result?.usable) { setCountUp(0); return }
    let frame
    const started = performance.now()
    const tick = (now) => {
      const t = Math.min(1, (now - started) / 650)
      setCountUp(Math.round((1 - Math.pow(1 - t, 3)) * result.interference))
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, result])

  const trial = trials.current[index]
  const isPractice = trial?.practice
  const scoredNumber = index - PRACTICE_PER_CONDITION * 2 + 1

  const usable = result?.usable
  const tier = usable ? tierFor(result.interference) : null
  const z = usable ? (result.interference - INTERFERENCE_MEAN) / INTERFERENCE_SD : 0
  // Share of people expected to show this little interference or less.
  const topPercent = normalCDF(z) * 100
  const beatPercent = Math.max(1, Math.min(99, Math.round(100 - topPercent)))

  return (
    <div ref={rootRef} className="bg-white border border-gray-200 rounded-3xl shadow-xl overflow-hidden">
      {phase !== 'finished' ? (
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 px-6 sm:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-semibold text-blue-200 uppercase tracking-wide">
                {phase === 'running' && isPractice ? 'Practice' : 'Round'}
              </p>
              <p className="text-3xl font-bold text-white tabular-nums leading-none">
                {phase !== 'running'
                  ? `0/${SCORED_TOTAL}`
                  : isPractice
                    ? `${index + 1}/${PRACTICE_PER_CONDITION * 2}`
                    : `${scoredNumber}/${SCORED_TOTAL}`}
              </p>
            </div>
            <p className="text-sm font-semibold text-white/90 text-right">
              {phase === 'idle' ? 'Ready when you are' : 'Pick the ink colour'}
            </p>
          </div>

          <div
            className={`relative mx-auto w-full max-w-md h-32 sm:h-48 rounded-2xl bg-gray-900 flex items-center justify-center ring-4 transition-colors ${
              stage === 'error' && phase === 'running' ? 'ring-rose-400' : 'ring-transparent'
            }`}
            aria-live="polite"
          >
            {phase === 'idle' && (
              <span className="text-5xl sm:text-6xl font-black tracking-wide" style={{ color: colourById.blue.ink }}>
                RED
              </span>
            )}
            {phase === 'running' && stage === 'fixation' && (
              <span className="text-4xl font-light text-gray-500">+</span>
            )}
            {phase === 'running' && stage !== 'fixation' && trial && (
              <span
                className="text-5xl sm:text-6xl font-black tracking-wide select-none"
                style={{ color: colourById[trial.ink].ink }}
              >
                {colourById[trial.word].word}
              </span>
            )}
            {phase === 'running' && stage === 'error' && trial && (
              <span className="absolute bottom-3 text-sm font-semibold text-rose-300">
                The ink was {colourById[trial.ink].name.toLowerCase()}
              </span>
            )}
          </div>

          {phase === 'idle' ? (
            <div className="text-center mt-7">
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-bold text-lg px-10 py-4 rounded-2xl shadow-lg transition-all"
              >
                <Palette className="w-5 h-5 mr-2" />
                Start
              </button>
              <p className="text-blue-100 text-sm mt-4 max-w-sm mx-auto leading-relaxed">
                Ignore what the word says and pick the colour of the ink. That
                one is blue. Four practice rounds, then {SCORED_TOTAL} that count.
                Keys R, G, B and Y work too.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-md mx-auto">
              {COLOURS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onPointerDown={(e) => { e.preventDefault(); respond(c.id) }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); respond(c.id) } }}
                  className="relative h-16 rounded-xl font-bold text-gray-900 shadow-md active:scale-95 transition-transform touch-manipulation"
                  style={{ backgroundColor: c.ink }}
                >
                  {c.name}
                  <span className="hidden sm:block absolute top-1 right-2 text-[10px] font-semibold text-gray-900/50 uppercase">
                    {c.key}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : usable ? (
        <>
          <div className={`bg-gradient-to-br ${tier.ring} px-6 sm:px-8 py-10 text-center`}>
            {isNewBest && (
              <span className="inline-block px-3 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs font-bold tracking-wide mb-4">
                NEW PERSONAL BEST
              </span>
            )}
            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">
              Stroop interference
            </p>
            <p className="text-8xl sm:text-9xl font-bold text-white tabular-nums leading-none drop-shadow-sm">
              {countUp}
              <span className="text-3xl sm:text-4xl font-bold text-white/80 ml-2">ms</span>
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">{tier.label}</p>
            <p className="text-white/80 mt-1">{tier.sub}</p>
            <div className="flex items-center justify-center gap-2 flex-wrap mt-5">
              {topPercent <= 25 && (
                <span className="inline-block px-4 py-1.5 rounded-full bg-white text-gray-900 text-sm font-bold tabular-nums shadow-sm">
                  TOP {Math.max(1, Math.round(topPercent))}%
                </span>
              )}
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold tabular-nums">
                Less distracted than about {beatPercent}% of people
              </span>
            </div>
            <p className="text-white/70 text-xs mt-3 tabular-nums">
              Matching words {result.matchingMs} ms · Clashing words {result.clashingMs} ms · {Math.round(result.accuracy * 100)}% correct
            </p>
          </div>

          <ResultFooter onRestart={start} best={best} note="That comparison is an estimate from how people typically perform on keypress versions of the Stroop task, not a normed percentile, because no standardised sample exists for a browser version. Only correct answers are timed." />
        </>
      ) : (
        <>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 sm:px-8 py-10 text-center">
            <p className="text-xs font-semibold text-white/70 uppercase tracking-[0.2em] mb-2">Correct answers</p>
            <p className="text-8xl sm:text-9xl font-bold text-white tabular-nums leading-none drop-shadow-sm">
              {result.correct}<span className="text-4xl text-white/80">/{result.total}</span>
            </p>
            <p className="text-3xl sm:text-4xl font-bold text-white mt-4 tracking-tight">Too many misses to score</p>
            <p className="text-white/85 mt-2 max-w-sm mx-auto leading-relaxed">
              The score compares your speed on correct answers, so it needs at
              least {Math.round(MIN_ACCURACY * 100)}% right. Accuracy first, then speed.
            </p>
          </div>

          <ResultFooter onRestart={start} best={best} />
        </>
      )}

      {phase === 'finished' && (
        <TestPromptPopup
          delayMs={4000}
          storageKey="biqt_stroop_prompt"
          headline="Focus measured. Now the reasoning."
          body={
            <>
              You just measured how well you block out distraction. Our
              50-question assessment covers verbal, numerical and spatial
              reasoning, and gives you a full-scale score with your exact
              percentile.
            </>
          }
        />
      )}
    </div>
  )
}

function ResultFooter({ onRestart, best, note }) {
  return (
    <div className="p-6 sm:p-8">
      <Link
        href="/test"
        className="group relative block w-full overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-[length:200%_100%] hover:bg-[position:100%_0] p-[3px] shadow-xl shadow-purple-500/25 transition-[background-position] duration-700"
      >
        <span className="block rounded-[13px] bg-white px-6 py-5 text-center transition-colors group-hover:bg-transparent">
          <span className="block text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent group-hover:text-white transition-colors">
            Now find your actual IQ
          </span>
          <span className="mt-1 flex items-center justify-center text-sm font-medium text-gray-500 group-hover:text-blue-100 transition-colors">
            50 questions · all four domains · instant score
            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
          </span>
        </span>
      </Link>

      <p className="text-sm text-gray-600 text-center leading-relaxed mt-5">
        This is inhibitory control, the ability to override an automatic
        response. It is one part of executive function, not a measure of
        reasoning, and reasoning is most of what an IQ score is made of.
      </p>

      <div className="flex items-center justify-center gap-5 mt-5">
        <button
          type="button"
          onClick={onRestart}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 font-semibold text-sm transition-colors"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" />
          Play again
        </button>
        {best !== null && (
          <span className="text-sm text-gray-400 tabular-nums">Best: {best} ms</span>
        )}
      </div>

      {note && <p className="text-xs text-gray-400 mt-5 text-center leading-relaxed">{note}</p>}
    </div>
  )
}
